import { useTRPC } from '@/lib/trpc'
import { useAccountTimeZone } from '@/hooks/use-account-time-zone'
import type { GameStatusData, NextInput, SubmitActionInput } from '@/lib/types'
import { useMutation, useQuery } from '@tanstack/react-query'
import { Button } from '@/components/ui/button'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import type { ImperativePanelHandle } from 'react-resizable-panels'
import {
  useLocation,
  useNavigate,
  useParams,
  useSearchParams,
} from 'react-router-dom'
import { useMediaQuery } from 'usehooks-ts'

import {
  ResizableHandle,
  ResizablePanel,
  ResizablePanelGroup,
} from '@/components/ui/resizable'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { ActionPanel } from '@/components/play/ActionPanel'
import { DelegationToggle } from '@/components/play/DelegationToggle'
import { TakeoverCountdown } from '@/components/play/takeover-countdown'
import { DraftNotes } from '@/components/play/DraftNotes'
import { CompactPlaySidebar } from '@/components/play/compact-play-sidebar'
import { GameLog } from '@/components/play/GameLog'
import { GameStatus } from '@/components/play/GameStatus'
import { RetryCard } from '@/components/play/RetryCard'
import { TournamentGameState } from '@/components/play/tournament-game-state'
import { TournamentGameForfeit } from '@/components/tournament/game-forfeit'
import { tournamentUrl } from '@/components/tournament/edition'
import { GameNotificationSetupNotice } from '@/components/play/home-screen-notification-notice'
import { PlayerList, type LogView } from '@/components/play/PlayerList'
import type {
  ReplaceRoleHypothesesInput,
  ReplaceRoleHypothesesResult,
} from '@/components/play/role-hypothesis-picker'
import { usePlayStatus } from '@/hooks/use-play-status'
import { useRoleActionLifecycle } from '@/hooks/use-role-action-lifecycle'
import { useI18n } from '@/lib/i18n'
import { releaseAnonymousFreeGame } from '@/lib/anonymous-free-game-usage'
import { useAuth } from '@/lib/auth'
import {
  perspectiveTokenFingerprint,
  perspectiveTokenFromHash,
} from '@/lib/perspective-share'
import { selectCompletedPlayerPerspective } from '@/lib/completed-role-board-perspective'
import { selectGameRetryCard } from '@/lib/game-retry-card'
import { useGameSeo } from '@/lib/game-seo'
import {
  resolveWebPushGameDestination,
  webPushGameIdFromLocation,
} from '@/lib/notification-destination'
import { isNightSubPhase, SUBPHASE_I18N_KEYS } from '@/lib/play'
import { stripLanguagePrefix } from '@/lib/url-language-utils'

import type { RoundSubPhaseType } from '@repo/db'
import { toast } from 'sonner'

type OwnedNextInput = NonNullable<NextInput> & {
  actionId: string
  playerId: string
}

function readOwnedNextInput(
  data: GameStatusData | undefined,
): OwnedNextInput | null {
  const input = data?.nextInput
  return input && input.actionId && input.playerId
    ? { ...input, actionId: input.actionId, playerId: input.playerId }
    : null
}

function hallReturnTarget(
  returnTo: string | null,
  editionId: string,
): string | null {
  if (!returnTo || !returnTo.startsWith('/') || returnTo.startsWith('//'))
    return null
  try {
    const target = new URL(returnTo, window.location.origin)
    if (
      target.origin !== window.location.origin
      || stripLanguagePrefix(target.pathname) !== '/tournaments/hall'
      || target.searchParams.get('edition') !== editionId
    ) {
      return null
    }
    return `${target.pathname}${target.search}${target.hash}`
  }
  catch {
    return null
  }
}

export function DesktopGamePage() {
  const trpc = useTRPC()
  const { t } = useI18n()
  const { authResolved, browserSessionId, isAnonymous, user } = useAuth()
  const { initialize: initializeAccountTimeZone } = useAccountTimeZone()
  const { gameId = '' } = useParams<{ gameId: string }>()
  const location = useLocation()
  const navigate = useNavigate()
  const [searchParams] = useSearchParams()
  const perspectiveToken = useMemo(
    () => perspectiveTokenFromHash(location.hash),
    [location.hash],
  )
  const sharedViewer = perspectiveToken !== null
  const [perspectiveIdentity, setPerspectiveIdentity] = useState<{
    token: string
    fingerprint: string
  } | null>(null)
  useEffect(() => {
    let cancelled = false
    setPerspectiveIdentity(null)
    if (!perspectiveToken) {
      return () => {
        cancelled = true
      }
    }
    void perspectiveTokenFingerprint(perspectiveToken).then((fingerprint) => {
      if (!cancelled)
        setPerspectiveIdentity({ token: perspectiveToken, fingerprint })
    })
    return () => {
      cancelled = true
    }
  }, [perspectiveToken])
  const perspectiveFingerprint
    = perspectiveIdentity?.token === perspectiveToken
      ? perspectiveIdentity.fingerprint
      : null
  const notificationGameId = webPushGameIdFromLocation(
    stripLanguagePrefix(window.location.pathname),
    window.location.search,
  )
  const [notificationDestinationState, setNotificationDestinationState]
    = useState<'checking' | 'available'>(
      notificationGameId ? 'checking' : 'available',
    )
  const requestedLogView = searchParams.get('view')
  const requestedSeat = searchParams.get('seat')
  const parsedRequestedSeat
    = requestedSeat === null ? null : Number(requestedSeat)
  const requestedSeatPosition
    = parsedRequestedSeat !== null
      && Number.isSafeInteger(parsedRequestedSeat)
      && parsedRequestedSeat > 0
      && String(parsedRequestedSeat) === requestedSeat
      ? parsedRequestedSeat
      : null
  const [outOfCredits, setOutOfCredits] = useState(false)

  const [isNotesCollapsed, setIsNotesCollapsed] = useState(false)
  const [compactSidebarPanel, setCompactSidebarPanel] = useState<
    'players' | 'notes' | null
  >(null)
  const notesPanelRef = useRef<ImperativePanelHandle>(null)
  const isWideViewport = useMediaQuery('(min-width: 1024px)')
  const isDesktop = isWideViewport
  const { data: isAdmin } = useQuery(trpc.userRouter.isAdmin.queryOptions())

  const statusQuery = usePlayStatus(gameId, {
    perspectiveToken,
    perspectiveFingerprint,
  })
  const [tournamentGameId, setTournamentGameId] = useState<string | null>(null)
  useEffect(() => {
    if (statusQuery.data?.game.tournamentEditionId)
      setTournamentGameId(gameId)
  }, [gameId, statusQuery.data?.game.tournamentEditionId])
  useGameSeo()
  const { mutateAsync: replaceRoleHypothesesAsync } = useMutation(
    trpc.playRouter.replaceRoleHypotheses.mutationOptions(),
  )
  const handleReplaceRoleHypotheses = useCallback(
    async (
      input: ReplaceRoleHypothesesInput,
    ): Promise<ReplaceRoleHypothesesResult> => {
      if (sharedViewer)
        throw new Error('Shared viewers cannot update role hypotheses')
      try {
        return await replaceRoleHypothesesAsync({
          gameId,
          ...input,
        })
      }
      finally {
        await statusQuery.refetch().catch(() => {})
      }
    },
    [gameId, replaceRoleHypothesesAsync, sharedViewer, statusQuery],
  )
  const { mutateAsync: resolveGameDestinationAccess } = useMutation(
    trpc.webPushRouter.gameDestinationAccess.mutationOptions(),
  )

  useEffect(() => {
    if (!notificationGameId || !authResolved || !user || !browserSessionId)
      return
    let cancelled = false
    void resolveWebPushGameDestination(
      {
        expectedUserId: user.id,
        expectedSessionId: browserSessionId,
        gameId: notificationGameId,
      },
      resolveGameDestinationAccess,
    ).then((destination) => {
      if (cancelled)
        return
      if (destination === '/') {
        navigate(destination, { replace: true })
        return
      }
      setNotificationDestinationState('available')
      navigate(destination, { replace: true })
    })
    return () => {
      cancelled = true
    }
  }, [
    authResolved,
    browserSessionId,
    navigate,
    notificationGameId,
    resolveGameDestinationAccess,
    user,
  ])

  useEffect(() => {
    statusQuery.markApplied(statusQuery.data?.statusVersion ?? null)
  }, [statusQuery])

  const { mutateAsync: retryGameAsync, isPending: isRetryingGame }
    = useMutation(trpc.playRouter.retryGame.mutationOptions())
  const { mutateAsync: retryTournamentAsync, isPending: isRetryingTournament }
    = useMutation(trpc.tournamentRouter.retry.mutationOptions())
  const isRetrying = isRetryingGame || isRetryingTournament

  const handleRetry = useCallback(async () => {
    if (sharedViewer)
      return
    const editionId = statusQuery.data?.game.tournamentEditionId
    try {
      await initializeAccountTimeZone()
      await (editionId ? retryTournamentAsync({ editionId }) : retryGameAsync({ gameId }))
      await statusQuery.refetch()
    }
    catch {
      toast.error(t('play.retry.failed'))
      if (editionId)
        await statusQuery.refetch()
    }
  }, [
    initializeAccountTimeZone,
    gameId,
    retryGameAsync,
    retryTournamentAsync,
    sharedViewer,
    statusQuery,
    t,
  ])

  const actionLifecycle = useRoleActionLifecycle({
    gameId,
    input: sharedViewer ? null : readOwnedNextInput(statusQuery.data),
    status: statusQuery,
    readInput: readOwnedNextInput,
    prepare: (_freshInput, responses: SubmitActionInput['responses']) => ({
      responses,
      receipt: true,
    }),
  })
  const isSubmitting
    = actionLifecycle.notice === 'checking'
      || actionLifecycle.notice === 'processing'

  useEffect(() => {
    if (actionLifecycle.notice === 'credits')
      setOutOfCredits(true)
  }, [actionLifecycle.notice])

  const handleOutOfCreditsOpenChange = (open: boolean) => {
    setOutOfCredits(open)
    if (!open)
      void actionLifecycle.refresh()
  }

  const handleSubmit = async (responses: SubmitActionInput['responses']) => {
    if (sharedViewer)
      throw new Error('Shared viewers cannot submit actions')
    if (!gameId) {
      throw new Error('No game id')
    }
    await actionLifecycle.confirm(responses)
  }

  const renderActionFeedback = () => {
    const { notice } = actionLifecycle
    if (notice !== 'failed' && notice !== 'unknown' && notice !== 'stale')
      return null
    const recoverable = notice === 'unknown' || notice === 'stale'
    return (
      <div
        className="mb-2 rounded-md border border-amber-400/50 bg-amber-50 px-3 py-2 text-sm text-amber-950 dark:bg-amber-950/40 dark:text-amber-100"
        data-testid="desktop-action-feedback"
        role={notice === 'failed' ? 'alert' : 'status'}
      >
        <p>{t(`play.actionNotice.${notice}`)}</p>
        {recoverable && (
          <button
            type="button"
            className="mt-2 rounded border border-current px-2 py-1 font-medium"
            onClick={() => {
              void actionLifecycle.refresh(true)
            }}
          >
            {t('play.refreshStatus')}
          </button>
        )}
      </div>
    )
  }

  const mySeatState = useMemo(() => {
    const mine = statusQuery.data?.humanPlayer
    if (!mine)
      return null
    return (
      statusQuery.data?.players?.find(p => p.id === mine.id)?.seatState
      ?? null
    )
  }, [statusQuery.data])
  const hasDelegationControl
    = !sharedViewer
      && !statusQuery.data?.game.tournamentEditionId
      && (mySeatState === 'human' || mySeatState === 'delegated')
  const takeoverCountdown
    = !sharedViewer && statusQuery.data?.turnClock ? (
      <TakeoverCountdown
        variant="inline"
        turnClock={statusQuery.data.turnClock}
        waitingOn={
          statusQuery.data.game.shouldHideSubphaseDetail
            ? []
            : statusQuery.data.waitingOn
        }
      />
    ) : null
  const delegationControl
    = statusQuery.data && gameId && hasDelegationControl ? (
      <DelegationToggle
        gameId={gameId}
        seatState={mySeatState}
        onChanged={() => {
          statusQuery.refetch().catch(() => {})
        }}
      />
    ) : null

  const canAct = useMemo(() => {
    if (sharedViewer)
      return false
    const next = statusQuery.data?.nextInput
    if (!next)
      return false

    return (next.options?.length ?? 0) > 0
  }, [sharedViewer, statusQuery.data])

  const humanLog: string = useMemo(() => {
    return statusQuery.data?.humanLog || ''
  }, [statusQuery.data?.humanLog])

  const handleNotesCollapsedChange = (collapsed: boolean) => {
    if (collapsed)
      notesPanelRef.current?.collapse()
    else notesPanelRef.current?.expand()
    setIsNotesCollapsed(collapsed)
  }

  useEffect(() => {
    if (!isDesktop)
      setIsNotesCollapsed(false)
  }, [isDesktop])

  useEffect(() => {
    if (isDesktop)
      setCompactSidebarPanel(null)
  }, [isDesktop])

  const gameStatus = statusQuery.data?.game?.status

  const retryCard = useMemo(() => {
    const game = statusQuery.data?.game
    if (sharedViewer
      || (game?.tournamentEditionId && game.tournamentState !== 'IN_PROGRESS')) {
      return null
    }
    return selectGameRetryCard({
      gameStatus: game?.retryLater ? 'error' : gameStatus,
      retry: game?.retry,
    })
  }, [gameStatus, sharedViewer, statusQuery.data?.game])
  const gameIsCompleted = gameStatus === 'completed'
  const [liveVisitGameId, setLiveVisitGameId] = useState<string | null>(null)
  useEffect(() => {
    if (
      statusQuery.data?.game.id === gameId
      && (gameStatus === 'in_progress' || gameStatus === 'paused')
    ) {
      setLiveVisitGameId(gameId)
    }
  }, [gameId, gameStatus, statusQuery.data?.game.id])
  const preserveLiveRecord
    = !sharedViewer
      && gameIsCompleted
      && liveVisitGameId === gameId
      && requestedLogView === null

  useEffect(() => {
    if (isAnonymous && gameStatus === 'error' && gameId)
      releaseAnonymousFreeGame(gameId)
  }, [gameId, gameStatus, isAnonymous])

  const showLogPerspectiveControls
    = (gameIsCompleted
      || (!!statusQuery.data?.game.tournamentEditionId
        && statusQuery.data.game.tournamentState !== null))
      && !sharedViewer
      && statusQuery.data?.viewer.canSeeFullReplay !== false
  const players = statusQuery.data?.players ?? []
  const ownedLogPlayer = statusQuery.data?.humanPlayer
    ? players.find(player => player.id === statusQuery.data?.humanPlayer?.id)
    : undefined
  const fallbackLogPlayer = ownedLogPlayer ?? players[0]
  const requestedLogPlayer
    = requestedSeatPosition === null
      ? undefined
      : players.find(player => player.position === requestedSeatPosition)
  let completedLogView: LogView = 'god'
  if (
    sharedViewer
    || statusQuery.data?.viewer.canSeeFullReplay === false
    || requestedLogView === 'player'
    || (requestedLogView !== 'god' && statusQuery.data?.humanPlayer)
  ) {
    completedLogView = 'player'
  }
  const selectedLogPlayer
    = requestedLogView === 'player'
      && statusQuery.data?.viewer.canSeeFullReplay !== false
      ? (requestedLogPlayer ?? fallbackLogPlayer)
      : fallbackLogPlayer

  useEffect(() => {
    if (!showLogPerspectiveControls)
      return

    const canonicalSearchParams = new URLSearchParams(searchParams)
    if (requestedLogView === 'god' && requestedSeat !== null) {
      canonicalSearchParams.delete('seat')
      navigate(
        { search: `?${canonicalSearchParams.toString()}` },
        { replace: true },
      )
      return
    }
    if (
      requestedLogView !== 'player'
      || requestedLogPlayer
      || !fallbackLogPlayer
    ) {
      return
    }
    canonicalSearchParams.set('view', 'player')
    canonicalSearchParams.set('seat', String(fallbackLogPlayer.position))
    navigate(
      { search: `?${canonicalSearchParams.toString()}` },
      { replace: true },
    )
  }, [
    fallbackLogPlayer,
    navigate,
    requestedLogPlayer,
    requestedLogView,
    requestedSeat,
    searchParams,
    showLogPerspectiveControls,
  ])

  const completedPlayerLogQuery = useQuery({
    ...trpc.playRouter.completedPlayerLog.queryOptions({
      gameId,
      position: selectedLogPlayer?.position ?? 1,
    }),
    enabled:
      !!gameId
      && showLogPerspectiveControls
      && !sharedViewer
      && !preserveLiveRecord
      && completedLogView === 'player'
      && !!selectedLogPlayer,
    staleTime: Infinity,
    retry: false,
  })

  const isCompletedPlayerView
    = showLogPerspectiveControls
      && !preserveLiveRecord
      && completedLogView === 'player'
  const completedPlayerResponse = completedPlayerLogQuery.data
  const completedPlayerPerspective = selectCompletedPlayerPerspective({
    data: completedPlayerResponse,
    isError: completedPlayerLogQuery.isError,
    isPending: completedPlayerLogQuery.isPending,
    selectedSeat: selectedLogPlayer,
    statusRoleBoard: statusQuery.data?.roleBoard,
  })
  const selectedLogState = !selectedLogPlayer
    ? 'unavailable'
    : completedPlayerLogQuery.isPending
      ? 'loading'
      : completedPlayerLogQuery.isError || !completedPlayerPerspective
        ? 'unavailable'
        : completedPlayerPerspective.log.trim()
          ? 'ready'
          : 'empty'
  const displayedPlayerLog = isCompletedPlayerView
    ? selectedLogState === 'loading'
      ? t('play.logView.loading')
      : selectedLogState === 'unavailable' || selectedLogState === 'empty'
        ? t('play.logView.unavailable')
        : (completedPlayerPerspective?.log ?? t('play.logView.unavailable'))
    : humanLog
  const displayedPlayerJsonLog = isCompletedPlayerView
    ? selectedLogState === 'ready' || selectedLogState === 'empty'
      ? (completedPlayerPerspective?.playerJsonLog ?? null)
      : null
    : undefined
  const completedPlayerPerspectiveAccepted
    = selectedLogState === 'ready' || selectedLogState === 'empty'
  const completedRoleBoardOverride = !showLogPerspectiveControls
    ? undefined
    : preserveLiveRecord || completedLogView === 'god'
      ? (statusQuery.data?.roleBoard ?? null)
      : completedPlayerPerspectiveAccepted
        ? (completedPlayerPerspective?.roleBoard ?? null)
        : null
  const displayedPlayerLogIdentity = sharedViewer
    ? `shared-seat-${perspectiveFingerprint ?? 'pending'}`
    : isCompletedPlayerView
      ? `completed-seat-${selectedLogPlayer?.id ?? 'unavailable'}:${selectedLogState}`
      : `live-seat-${statusQuery.data?.humanPlayer?.id ?? 'spectator'}`

  const isGodView
    = !sharedViewer
      && (showLogPerspectiveControls
        ? completedLogView === 'god'
        : !statusQuery.data?.humanPlayer)

  const updateLogView = (view: LogView, position?: number) => {
    if (!showLogPerspectiveControls || sharedViewer)
      return
    const nextSearchParams = new URLSearchParams(searchParams)
    nextSearchParams.set('view', view)
    if (view === 'player') {
      const nextPosition = position ?? selectedLogPlayer?.position
      if (!nextPosition)
        return
      nextSearchParams.set('seat', String(nextPosition))
    }
    else {
      nextSearchParams.delete('seat')
    }
    if (nextSearchParams.toString() === searchParams.toString())
      return
    navigate({ search: `?${nextSearchParams.toString()}` })
  }

  const playerList = (
    <PlayerList
      data={statusQuery.data}
      onReplaceRoleHypotheses={
        sharedViewer ? undefined : handleReplaceRoleHypotheses
      }
      showRoles={Boolean(isAdmin) && !sharedViewer}
      showLogPerspectiveControls={showLogPerspectiveControls}
      logView={completedLogView}
      onLogViewChange={view => updateLogView(view)}
      roleBoardOverride={completedRoleBoardOverride}
      enablePlayerLogSelection={isCompletedPlayerView}
      viewedPlayerId={selectedLogPlayer?.id}
      onPlayerClick={player => updateLogView('player', player.position)}
    />
  )
  const gameLog = (
    <GameLog
      playerLog={displayedPlayerLog}
      playerJsonLog={displayedPlayerJsonLog}
      playerLogIdentity={displayedPlayerLogIdentity}
      godLog={sharedViewer ? undefined : statusQuery.data?.game.godLog}
      status={statusQuery.data}
      isGodView={isGodView}
      preserveLiveRecord={preserveLiveRecord}
    />
  )
  const renderActionPanel = (className?: string) =>
    statusQuery.data
    && !sharedViewer
    && (!statusQuery.data.game.tournamentEditionId
      || (statusQuery.data.game.tournamentState === 'IN_PROGRESS'
        && statusQuery.data.game.tournamentCompetitionOpen)) ? (
          <ActionPanel
            className={className}
            nextInput={statusQuery.data.nextInput ?? null}
            isSubmitting={isSubmitting}
            canAct={canAct && !actionLifecycle.blocked}
            roundName={t(
              SUBPHASE_I18N_KEYS[
                statusQuery.data.game.subPhase as RoundSubPhaseType
              ],
            )}
            gameData={statusQuery.data}
            waitingOn={statusQuery.data.waitingOn}
            connection={{
              delivery: statusQuery.delivery,
              isFetchFailing: statusQuery.isError,
              lastSuccessAt: statusQuery.dataUpdatedAt || null,
            }}
            onSubmit={handleSubmit}
          />
        ) : null
  const renderGameStatus = () => (
    <GameStatus
      data={statusQuery.data}
      seatControl={delegationControl}
      takeoverCountdown={takeoverCountdown}
      compact={!isDesktop}
      sharedViewer={sharedViewer}
    />
  )
  const villageTime
    = statusQuery.data?.game.subPhase
      && !isNightSubPhase(statusQuery.data.game.subPhase as RoundSubPhaseType)
      ? 'day'
      : 'night'

  if (notificationGameId && notificationDestinationState !== 'available') {
    return null
  }

  if ((sharedViewer || searchParams.has('tournament') || tournamentGameId === gameId)
    && (!statusQuery.data || statusQuery.isError)) {
    const unavailable = statusQuery.isError
    return (
      <div
        className="flex min-h-screen items-center justify-center bg-background px-4 pt-16"
        data-testid="shared-play-state"
      >
        <div
          className="w-full max-w-md rounded-xl border border-border bg-card p-6 text-center shadow-sm"
          role={unavailable ? 'alert' : 'status'}
        >
          <p className="text-sm text-foreground">
            {t(
              sharedViewer
                ? unavailable
                  ? 'play.sharedView.unavailable'
                  : 'play.sharedView.loading'
                : unavailable ? 'common.error' : 'common.loading',
            )}
          </p>
          {unavailable && (
            <button
              type="button"
              className="mt-4 rounded-md border border-border px-3 py-2 text-sm hover:bg-accent"
              onClick={() => {
                void statusQuery.refetch()
              }}
            >
              {t('play.refreshStatus')}
            </button>
          )}
        </div>
      </div>
    )
  }

  return (
    <div
      className="flex min-h-screen w-full flex-col overflow-x-clip bg-background/95 bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-primary/10 via-background to-background lg:h-screen lg:overflow-y-auto lg:overscroll-contain lg:[scrollbar-gutter:stable]"
      data-testid="desktop-game-page"
    >
      {}
      <AlertDialog
        open={outOfCredits}
        onOpenChange={handleOutOfCreditsOpenChange}
      >
        <AlertDialogContent data-testid="out-of-credits-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>{t('play.outOfCredits.title')}</AlertDialogTitle>
            <AlertDialogDescription>
              {t(
                statusQuery.data?.turnClock
                  ? 'play.outOfCredits.body'
                  : 'play.outOfCredits.bodyNoTakeover',
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="out-of-credits-later">
              {t('play.outOfCredits.later')}
            </AlertDialogCancel>
            <AlertDialogAction
              data-testid="out-of-credits-topup"
              onClick={() => navigate('/dashboard?tab=subscription')}
            >
              {t('play.outOfCredits.topUp')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <div
        className="flex w-full flex-col gap-2 overflow-y-auto pb-4 pt-20 lg:min-h-screen lg:overflow-visible"
        data-testid="play-content-shell"
      >
        {statusQuery.data?.game.tournamentEditionId && (
          <Button
            type="button"
            variant="outline"
            data-testid="tournament-return"
            className="h-auto min-h-11 max-w-full self-start whitespace-normal"
            onClick={() =>
              navigate(
                tournamentUrl(statusQuery.data!.game.tournamentEditionId!),
              )}
          >
            {t('tournamentRules.backToTournament')}
          </Button>
        )}
        {statusQuery.data?.game.tournamentEditionId
          && (() => {
            const target = hallReturnTarget(
              searchParams.get('returnTo'),
              statusQuery.data.game.tournamentEditionId,
            )
            if (!target)
              return null
            return (
              <Button
                type="button"
                variant="outline"
                data-testid="tournament-hall-return"
                className="h-auto min-h-11 max-w-full self-start whitespace-normal"
                onClick={() => navigate(target)}
              >
                {t('tournamentHall.title')}
              </Button>
            )
          })()}
        {!sharedViewer && statusQuery.data?.game.tournamentEditionId && statusQuery.data.game.tournamentState === 'IN_PROGRESS' && (
          <div className="flex flex-wrap gap-2">
            <TournamentGameForfeit
              key={`${gameId}:${statusQuery.data.game.tournamentEditionId}`}
              editionId={statusQuery.data.game.tournamentEditionId}
              gameId={gameId}
              onSettled={statusQuery.refetch}
            />
          </div>
        )}
        <GameNotificationSetupNotice />
        {isDesktop && renderGameStatus()}

        {retryCard && (
          <RetryCard
            retry={retryCard.retry}
            isErrored={retryCard.isErrored}
            isRetrying={isRetrying}
            onRetry={handleRetry}
            tournament={!!statusQuery.data?.game.tournamentEditionId}
          />
        )}
        {statusQuery.data?.game.tournamentState === 'VOID' && (
          <TournamentGameState
            state="void"
            canContinue={statusQuery.data.game.tournamentCanContinue}
          />
        )}

        {isDesktop ? (
          <ResizablePanelGroup
            direction="horizontal"
            className="!h-auto min-h-[calc(100dvh-11rem)] flex-none !overflow-visible"
            data-testid="play-desktop-layout"
          >
            <ResizablePanel
              ref={notesPanelRef}
              id="play-notes-panel"
              order={1}
              defaultSize={20}
              minSize={16}
              maxSize={35}
              collapsible
              collapsedSize={4}
              onCollapse={() => setIsNotesCollapsed(true)}
              onExpand={() => setIsNotesCollapsed(false)}
              className="min-w-0"
              data-testid="draft-notes-column"
            >
              <div className="flex h-[calc(100dvh-11rem)] min-h-0 flex-col">
                <DraftNotes
                  gameId={gameId}
                  collapsed={isNotesCollapsed}
                  onCollapsedChange={handleNotesCollapsedChange}
                />
              </div>
            </ResizablePanel>

            <ResizableHandle
              withHandle
              className="mx-2"
              aria-label={`${t('play.draftNotes')} / ${t('play.gameLog')}`}
              data-testid="notes-log-resize-handle"
            />

            <ResizablePanel
              id="play-game-log-panel"
              order={2}
              defaultSize={40}
              minSize={25}
              className="min-w-0"
            >
              <div className="flex h-[calc(100dvh-11rem)] min-h-0 flex-col">
                {gameLog}
              </div>
            </ResizablePanel>

            <ResizableHandle
              withHandle
              className="mx-2"
              aria-label={`${t('play.gameLog')} / ${t('play.playerList')}`}
              data-testid="log-players-resize-handle"
            />

            <ResizablePanel
              id="play-players-panel"
              order={3}
              defaultSize={40}
              minSize={30}
              className="min-w-0 !overflow-visible"
            >
              <div
                className="flex h-[calc(100dvh-11rem)] min-h-0 flex-col"
                data-testid="player-list-column"
              >
                <div className="shrink-0">{playerList}</div>

                <div
                  className="flex min-h-64 flex-1 flex-col justify-end overflow-hidden"
                  data-testid="player-controls-column"
                >
                  {renderActionFeedback()}
                  {renderActionPanel('mt-auto max-h-full')}
                </div>
              </div>
            </ResizablePanel>
          </ResizablePanelGroup>
        ) : (
          <div
            className="relative min-w-0"
            data-testid="play-mobile-layout"
            data-village-time={villageTime}
          >
            <main className="flex min-w-0 flex-col gap-2">
              <div data-testid="compact-game-status">{renderGameStatus()}</div>
              <div className="flex h-[50dvh] min-h-72 flex-col">{gameLog}</div>
              <div className="flex-none">
                {renderActionFeedback()}
                {renderActionPanel()}
              </div>
            </main>
            <CompactPlaySidebar
              activePanel={compactSidebarPanel}
              onActivePanelChange={setCompactSidebarPanel}
              players={playerList}
              notes={<DraftNotes gameId={gameId} showCollapseButton={false} />}
            />
          </div>
        )}
      </div>
    </div>
  )
}
